import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import { Add, Edit, Delete, ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

function Cajas() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [cajas, setCajas] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editando, setEditando] = useState(null);
  
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    saldo_inicial: 0
  });

  useEffect(() => {
    cargarCajas();
  }, []);

  const cargarCajas = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.get(`${API_URL}/api/tesoreria/cajas`, config);
      setCajas(response.data.data);
    } catch (err) {
      console.error('Error al cargar cajas:', err);
      setError('Error al cargar cajas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (caja = null) => {
    if (caja) {
      setEditando(caja);
      setFormData({
        nombre: caja.nombre,
        descripcion: caja.descripcion || '',
        saldo_inicial: caja.saldo_inicial
      });
    } else {
      setEditando(null);
      setFormData({
        nombre: '',
        descripcion: '',
        saldo_inicial: 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditando(null);
    setFormData({ nombre: '', descripcion: '', saldo_inicial: 0 });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editando) {
        await axios.put(
          `${API_URL}/api/tesoreria/cajas/${editando.id}`,
          formData,
          config
        );
        setSuccess('Caja actualizada exitosamente');
      } else {
        await axios.post(`${API_URL}/api/tesoreria/cajas`, formData, config);
        setSuccess('Caja creada exitosamente');
      }

      handleCloseDialog();
      cargarCajas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al guardar caja:', err);
      setError(err.response?.data?.message || 'Error al guardar caja');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleDelete = async (cajaId) => {
    if (!window.confirm('¿Está seguro de eliminar esta caja?')) return;

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.delete(`${API_URL}/api/tesoreria/cajas/${cajaId}`, config);
      setSuccess('Caja eliminada exitosamente');
      cargarCajas();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error al eliminar caja:', err);
      setError(err.response?.data?.message || 'Error al eliminar caja');
      setTimeout(() => setError(null), 3000);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-EC', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/tesoreria')} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          Gestión de Cajas
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Nueva Caja
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell align="right">Saldo Inicial</TableCell>
                <TableCell align="right">Saldo Actual</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!cajas || cajas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No hay cajas registradas
                  </TableCell>
                </TableRow>
              ) : (
                cajas.map((caja) => (
                  <TableRow key={caja.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">
                        {caja.nombre}
                      </Typography>
                    </TableCell>
                    <TableCell>{caja.descripcion || '-'}</TableCell>
                    <TableCell align="right">{formatCurrency(caja.saldo_inicial)}</TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body1" 
                        fontWeight="bold"
                        color={caja.saldo_actual >= 0 ? 'success.main' : 'error.main'}
                      >
                        {formatCurrency(caja.saldo_actual)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={caja.activa ? 'Activa' : 'Inactiva'}
                        color={caja.activa ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(caja)}
                        color="primary"
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(caja.id)}
                        color="error"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog Crear/Editar */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editando ? 'Editar Caja' : 'Nueva Caja'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Descripción"
                  value={formData.descripcion}
                  onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Saldo Inicial"
                  value={formData.saldo_inicial}
                  onChange={(e) => setFormData({ ...formData, saldo_inicial: parseFloat(e.target.value) })}
                  inputProps={{ step: 0.01, min: 0 }}
                  disabled={!!editando}
                  helperText={editando ? 'El saldo inicial no se puede modificar' : ''}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancelar</Button>
            <Button type="submit" variant="contained">
              {editando ? 'Actualizar' : 'Crear'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
}

export default Cajas;
